import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { isAdminAssistantRole } from '../../../lib/dentistSubmissionWizard';

const FilterPanel = ({ filters, onFilterChange, onClearFilters, resultCount }) => {
  const { userProfile: authUser } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dentistClients, setDentistClients] = useState([]);
  const [loadingDentists, setLoadingDentists] = useState(false);

  const canManageAllPatients = isAdminAssistantRole(authUser?.roleTypes);

  useEffect(() => {
    if (canManageAllPatients) {
      fetchDentistClients();
    }
  }, [canManageAllPatients]);

  const fetchDentistClients = async () => {
    try {
      setLoadingDentists(true);
      
      // Get all users with dentist_client role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('role_type', 'dentist_client')
        .single();

      if (roleError) throw roleError;

      // Get user role assignments for dentist_client role
      const { data: assignments, error: assignmentsError } = await supabase
        .from('user_role_assignments')
        .select('user_id')
        .eq('role_id', roleData.id)
        .eq('is_active', true);

      if (assignmentsError) throw assignmentsError;

      const userIds = assignments.map(a => a.user_id);

      // Get user profiles for these users
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, company_name')
        .in('id', userIds)
        .eq('is_active', true)
        .order('full_name');

      if (profilesError) throw profilesError;

      setDentistClients(profiles || []);
    } catch (err) {
      console.error('Error fetching dentist clients:', err);
    } finally {
      setLoadingDentists(false);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' },
  ];

  const caseStatusOptions = [
    { value: 'all', label: 'Tous les cas' },
    { value: 'with-active', label: 'Avec cas actifs' },
    { value: 'without-active', label: 'Sans cas actifs' }
  ];

  const dentistOptions = [
    { value: 'all', label: 'Tous les dentistes' },
    ...dentistClients.map(dentist => ({
      value: dentist.id,
      label: dentist.full_name || dentist.email,
      subtitle: dentist.company_name
    }))
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const activeFilterCount = Object.entries(filters)?.filter(
    ([key, value]) => {
      // Don't count 'all' or empty values
      if (value === 'all' || value === '') return false;
      // For location, only count if it has a value
      return true;
    }
  )?.length;

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Filtres</h3>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
          {resultCount !== null && resultCount !== undefined && (
            <span className="text-sm text-muted-foreground">
              ({resultCount} résultat{resultCount > 1 ? 's' : ''})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Effacer
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-4">
          <div className={`grid grid-cols-1 md:grid-cols-2 ${canManageAllPatients ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
            {canManageAllPatients && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dentiste / Client
                </label>
                {loadingDentists ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2 px-3 bg-muted border border-border rounded-lg">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Chargement...
                  </div>
                ) : (
                  <select
                    value={filters?.dentist || 'all'}
                    onChange={(e) => handleFilterChange('dentist', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm"
                  >
                    {dentistOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                        {option.subtitle && ` - ${option.subtitle}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Status Filter */}
            <Select
              label="Statut du patient"
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
            />

            {/* Case Status Filter */}
            <Select
              label="Statut des cas"
              options={caseStatusOptions}
              value={filters?.caseStatus}
              onChange={(value) => handleFilterChange('caseStatus', value)}
            />

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Localisation
              </label>
              <input
                type="text"
                value={filters?.location || ''}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                placeholder="Ville, région..."
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm"
              />
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Période d'inscription
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={filters?.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                  placeholder="Du"
                  className="text-sm"
                />
                <span className="text-muted-foreground text-sm">-</span>
                <Input
                  type="date"
                  value={filters?.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                  placeholder="Au"
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;